import { AxiosStatic, InternalAxiosRequestConfig } from 'axios';

import { authentication } from './authentication';
import { isInternal } from './utils';

export const ImpersonationHeaderKey = 'Impersonate-Client';
const ImpersonationStorageKey = 'impersonation';

interface ImpersonationData {
  client_id: string;
  client_user_group_ids: string[];
}

const setImpersonationStorage = (data: ImpersonationData) =>
  sessionStorage.setItem(ImpersonationStorageKey, JSON.stringify(data));

const getImpersonationStorage = (): ImpersonationData => JSON.parse(sessionStorage.getItem(ImpersonationStorageKey));

export function setImpersonationData(data: ImpersonationData | null) {
  const { client_id, client_user_group_ids } = data ?? {};
  setImpersonationStorage({ client_id, client_user_group_ids });
}

export function getImpersonationData(): ImpersonationData | null {
  const user = authentication.getUser();
  if (!isInternal(user)) {
    return null;
  }

  const { client_id, client_user_group_ids } = getImpersonationStorage() ?? {};
  if (client_id && client_user_group_ids) {
    return { client_id, client_user_group_ids };
  }

  return null;
}

interface InterceptorConfig {
  ignoreUrls?: string[];
}

export const addImpersonationInterceptor = (axios: AxiosStatic, config?: InterceptorConfig) => {
  const customName = 'impersonationInterceptor';

  if ((axios.interceptors.request as any).handlers?.some(h => h.fulfilled?.customName === customName)) {
    return;
  }

  const interceptor = (requestConfig: InternalAxiosRequestConfig) => {
    if (config?.ignoreUrls && config?.ignoreUrls.some(url => requestConfig.url.includes(url))) {
      return requestConfig;
    }

    const impersonationData = getImpersonationData();
    if (impersonationData) {
      requestConfig.headers[ImpersonationHeaderKey] = JSON.stringify(impersonationData);
    }

    return requestConfig;
  };

  interceptor.customName = customName;

  axios.interceptors.request.use(interceptor);
};

/**
 * Adds the impersonation header in a given headers object. Can be used with Apollo client.
 * Takes the headers object and adds the impersonation header if the impersonation data is present.
 *
 * Example usage as Appollo client middleware:
 * ```ts
 * export const impersonationMiddleware = new ApolloLink((operation, forward) => {
      operation.setContext(setImpersonationHeader)
      return forward(operation)
    });
 * ```
 */
export const setImpersonationHeader = ({ headers = {} }) => {
  const impersonationData = getImpersonationData();
  if (!impersonationData) {
    return { headers };
  }

  return {
    headers: {
      ...headers,
      [ImpersonationHeaderKey]: JSON.stringify(impersonationData),
    },
  };
};
